import type { HealthCheckFinding, HealthCheckResult } from '@/types/healthCheck';
import type { OrganisationTimesheetSettings } from '@/types/timesheetSettings';

// Industry benchmarks and best practices
// Note: Utilisation targets are based on common PSA industry practices
// Legal limits are based on actual regulations (e.g., EU Working Time Directive)
const INDUSTRY_BENCHMARKS = {
  utilisationTarget: {
    low: 60,
    standard: 75, // Common target in professional services
    high: 85,
  },
  weeklyHoursLimit: {
    euCompliant: 48, // EU Working Time Directive
    standard: 50,
  },
  deadlineDay: {
    common: 'Monday', // Common practice in professional services
  },
};

// Helper function to calculate days between two day names
const calculateDaysBetween = (startDay: string, endDay: string): number => {
  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const startIndex = days.indexOf(startDay);
  const endIndex = days.indexOf(endDay);
  
  if (startIndex === -1 || endIndex === -1) return 0;
  
  if (endIndex >= startIndex) {
    return endIndex - startIndex;
  } else {
    return (7 - startIndex) + endIndex;
  }
};

export const runHealthCheck = (
  settings: OrganisationTimesheetSettings
): HealthCheckResult => {
  const findings: HealthCheckFinding[] = [];

  // ============================================
  // CHECK 1: Deadline vs Week Start Conflict
  // ============================================
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  if (settings.deadline?.deadlineDay !== undefined && settings.weekStartDay !== undefined) {
    const deadlineDayName = dayNames[settings.deadline.deadlineDay] || 'Unknown';
    const weekStartDayName = dayNames[settings.weekStartDay] || 'Unknown';
    const daysBetween = calculateDaysBetween(
      weekStartDayName,
      deadlineDayName
    );
    
    if (daysBetween < 5) {
      findings.push({
        id: 'deadline-week-start-conflict',
        severity: 'warning',
        category: 'conflict',
        title: 'Short Submission Window',
        description: `Your weekly deadline is ${deadlineDayName} at ${settings.deadline.deadlineTime}, but your week starts on ${weekStartDayName}. This gives users only ${daysBetween} working days to submit.`,
        recommendation: 'Consider moving the deadline to Monday morning to give users the full weekend to review their timesheets.',
        affectedSetting: 'deadline.deadlineDay',
        affectedTab: 'submissions-deadlines',
        fixAction: {
          label: 'Set deadline to Monday 9:00 AM',
          value: { day: 1, time: '09:00' },
          field: 'deadlineDay',
        },
      });
    }
  }

  // ============================================
  // CHECK 2: Utilisation Target vs Industry Benchmark
  // ============================================
  if (settings.utilisation?.targetUtilisationRate) {
    const targetPercentage = Math.round(settings.utilisation.targetUtilisationRate * 100);
    const industryAverage = INDUSTRY_BENCHMARKS.utilisationTarget.standard;
    
    if (targetPercentage > INDUSTRY_BENCHMARKS.utilisationTarget.high) {
      findings.push({
        id: 'high-utilisation-target',
        severity: 'warning',
        category: 'benchmark',
        title: 'High Utilisation Target',
        description: `Your utilisation target (${targetPercentage}%) is higher than the commonly recommended target (${industryAverage}%) and exceeds the high-performance threshold (${INDUSTRY_BENCHMARKS.utilisationTarget.high}%).`,
        recommendation: 'High targets may lead to burnout and reduced quality of work. Consider whether this aligns with your company culture and employee wellbeing goals.',
        affectedSetting: 'utilisation.targetUtilisationRate',
        affectedTab: 'utilisation',
        fixAction: {
          label: `Set to recommended target (${industryAverage}%)`,
          value: industryAverage / 100,
          field: 'targetUtilisationRate',
        },
      });
    } else if (targetPercentage > industryAverage + 5) {
      findings.push({
        id: 'above-average-utilisation',
        severity: 'info',
        category: 'benchmark',
        title: 'Above Recommended Utilisation Target',
        description: `Your utilisation target (${targetPercentage}%) is above the commonly recommended target (${industryAverage}%).`,
        recommendation: 'This is achievable but may require strong project pipeline management and careful monitoring to maintain consistently.',
        affectedSetting: 'utilisation.targetUtilisationRate',
        affectedTab: 'utilisation',
      });
    } else if (targetPercentage < INDUSTRY_BENCHMARKS.utilisationTarget.low) {
      findings.push({
        id: 'low-utilisation-target',
        severity: 'info',
        category: 'benchmark',
        title: 'Below Industry Average Utilisation Target',
        description: `Your utilisation target (${targetPercentage}%) is below the typical range for professional services (${INDUSTRY_BENCHMARKS.utilisationTarget.low}-${INDUSTRY_BENCHMARKS.utilisationTarget.high}%).`,
        recommendation: 'Low targets may indicate underutilization of resources. Consider whether this aligns with your business model.',
        affectedSetting: 'utilisation.targetUtilisationRate',
        affectedTab: 'utilisation',
      });
    }
  }

  // ============================================
  // CHECK 3: Legal Compliance (EU Working Time Directive)
  // ============================================
  if (settings.policyLimits?.legalMaxHoursPerWeek) {
    const limit = settings.policyLimits.legalMaxHoursPerWeek;
    
    if (limit > INDUSTRY_BENCHMARKS.weeklyHoursLimit.euCompliant) {
      findings.push({
        id: 'legal-limit-compliance',
        severity: 'error',
        category: 'compliance',
        title: 'Exceeds EU Working Time Regulations',
        description: `Your legal limit threshold (${limit} hours/week) exceeds the EU Working Time Directive (${INDUSTRY_BENCHMARKS.weeklyHoursLimit.euCompliant} hours/week average).`,
        recommendation: 'If you have employees in the EU, reduce the threshold to comply with EU regulations. Non-compliance can result in legal penalties.',
        affectedSetting: 'policyLimits.legalMaxHoursPerWeek',
        affectedTab: 'working-hours',
        fixAction: {
          label: `Set to ${INDUSTRY_BENCHMARKS.weeklyHoursLimit.euCompliant} hours (EU compliant)`,
          value: INDUSTRY_BENCHMARKS.weeklyHoursLimit.euCompliant,
          field: 'legalMaxHoursPerWeek',
        },
      });
    }
  }

  // ============================================
  // CHECK 4: Grace Period vs Reminder Days Before
  // ============================================
  if (settings.deadline?.gracePeriodDays !== undefined && 
      settings.deadline?.reminderDaysBefore !== undefined) {
    const gracePeriod = settings.deadline.gracePeriodDays;
    const reminderDays = settings.deadline.reminderDaysBefore;
    
    if (gracePeriod > reminderDays) {
      findings.push({
        id: 'grace-period-reminder-mismatch',
        severity: 'warning',
        category: 'usability',
        title: 'Grace Period Longer Than Reminder Days Before',
        description: `Your grace period (${gracePeriod} days) is longer than your reminder days before deadline (${reminderDays} days). Users may receive multiple reminders during the grace period, causing confusion.`,
        recommendation: 'Set grace period to be shorter than or equal to reminder days before deadline for clearer communication.',
        affectedSetting: 'deadline.gracePeriodDays',
        affectedTab: 'submissions-deadlines',
        fixAction: {
          label: `Set grace period to ${reminderDays} days`,
          value: reminderDays,
          field: 'gracePeriodDays',
        },
      });
    }
  }

  // ============================================
  // CHECK 5: (Removed - showDescription is not a separate setting;
  //           requireDescription controls both visibility and requirement)
  // ============================================

  // ============================================
  // CHECK 6: Role Required but Not Shown
  // ============================================
  if (settings.requireRole && !settings.showRole) {
    findings.push({
      id: 'role-visibility-conflict',
      severity: 'error',
      category: 'conflict',
      title: 'Role Required But Hidden',
      description: 'You require role selection for all time entries, but the role field is not visible in the time entry form.',
      recommendation: 'Enable "Show Role" to allow users to select required roles.',
      affectedSetting: 'showRole',
      affectedTab: 'general',
      fixAction: {
        label: 'Enable role field',
        value: true,
        field: 'showRole',
      },
    });
  }

  // ============================================
  // CHECK 7: (Removed - requireProject/showProject are not
  //           part of OrganisationTimesheetSettings)
  // ============================================

  // ============================================
  // CHECK 8: Copy Previous Week Optimization
  // ============================================
  if (!settings.deadline?.enableCopyPreviousWeek) {
    findings.push({
      id: 'copy-previous-week-disabled',
      severity: 'info',
      category: 'optimization',
      title: 'Copy Previous Week Feature Disabled',
      description: 'Enabling "Copy Previous Week" allows users to quickly duplicate their previous week\'s entries, streamlining time entry for users with recurring tasks or consistent weekly schedules.',
      recommendation: 'Consider enabling this feature to reduce repetitive data entry and improve user experience.',
      affectedSetting: 'deadline.enableCopyPreviousWeek',
      affectedTab: 'submissions-deadlines',
      fixAction: {
        label: 'Enable Copy Previous Week',
        value: true,
        field: 'enableCopyPreviousWeek',
      },
    });
  }

  // ============================================
  // CHECK 9: Historical Entry Limit
  // ============================================
  if (settings.historicalEntryLimit && settings.historicalEntryLimit > 12) {
    findings.push({
      id: 'high-historical-limit',
      severity: 'info',
      category: 'usability',
      title: 'High Historical Entry Limit',
      description: `Users can enter time up to ${settings.historicalEntryLimit} weeks ago. A high limit may allow stale entries that are difficult to verify.`,
      recommendation: 'Consider reducing the historical entry limit to ensure timely and accurate time entry.',
      affectedSetting: 'historicalEntryLimit',
      affectedTab: 'general',
    });
  }

  // ============================================
  // CHECK 10: Negative Hours Compliance Risk
  // ============================================
  if (settings.allowNegativeHours) {
    findings.push({
      id: 'negative-hours-compliance',
      severity: 'warning',
      category: 'compliance',
      title: 'Negative Hours Allowed',
      description: 'Allowing negative hours may violate billing compliance requirements in some industries and can complicate payroll processing.',
      recommendation: 'Disable negative hours unless you have a specific business need (e.g., time-off corrections). Consider using a dedicated time-off system instead.',
      affectedSetting: 'allowNegativeHours',
      affectedTab: 'general',
      fixAction: {
        label: 'Disable negative hours',
        value: false,
        field: 'allowNegativeHours',
      },
    });
  }

  // ============================================
  // CHECK 11: AI Insights Optimization
  // ============================================
  if (!settings.deadline?.enableAiInsights) {
    findings.push({
      id: 'ai-insights-disabled',
      severity: 'info',
      category: 'optimization',
      title: 'AI Insights Feature Disabled',
      description: 'AI-powered insights can help identify patterns, suggest improvements, and detect anomalies in time tracking data.',
      recommendation: 'Consider enabling AI insights to provide your team with intelligent recommendations and productivity analytics.',
      affectedSetting: 'deadline.enableAiInsights',
      affectedTab: 'submissions-deadlines',
      fixAction: {
        label: 'Enable AI Insights',
        value: true,
        field: 'enableAiInsights',
      },
    });
  }

  // ============================================
  // CHECK 12: Minimum Duration Too High
  // ============================================
  if (settings.minimumEntryDuration === '30min' || settings.minimumEntryDuration === '1hour') {
    findings.push({
      id: 'high-minimum-duration',
      severity: 'info',
      category: 'usability',
      title: 'High Minimum Time Entry Duration',
      description: `Your minimum time entry duration is ${settings.minimumEntryDuration}. This may prevent accurate tracking of short tasks.`,
      recommendation: 'Consider reducing to 15min or 6min for more granular time tracking.',
      affectedSetting: 'minimumEntryDuration',
      affectedTab: 'general',
      fixAction: {
        label: 'Set to 15 minutes',
        value: '15min',
        field: 'minimumEntryDuration',
      },
    });
  }

  // ============================================
  // CHECK 13: Future Entry Limit Too Restrictive
  // ============================================
  if (settings.allowFutureDatedEntries === 'no') {
    findings.push({
      id: 'restrictive-future-limit',
      severity: 'info',
      category: 'usability',
      title: 'Future Entries Disabled',
      description: 'Future-dated time entries are not allowed. This may prevent users from planning ahead or entering scheduled work.',
      recommendation: 'Consider allowing at least 1-2 weeks of future entries for better planning and flexibility.',
      affectedSetting: 'allowFutureDatedEntries',
      affectedTab: 'general',
      fixAction: {
        label: 'Set to 2 weeks',
        value: '2weeks',
        field: 'allowFutureDatedEntries',
      },
    });
  }

  // ============================================
  // CHECK 14: Daily Legal Max Hours Too High
  // ============================================
  if (settings.policyLimits?.legalMaxHoursPerDay && 
      settings.policyLimits.legalMaxHoursPerDay > 12) {
    findings.push({
      id: 'high-daily-legal-max',
      severity: 'warning',
      category: 'compliance',
      title: 'High Daily Legal Max Hours',
      description: `Your daily legal max hours (${settings.policyLimits.legalMaxHoursPerDay} hours) is very high and may not effectively flag overwork.`,
      recommendation: 'Consider setting a lower threshold (8-10 hours) to better monitor employee wellbeing and prevent burnout.',
      affectedSetting: 'policyLimits.legalMaxHoursPerDay',
      affectedTab: 'working-hours',
      fixAction: {
        label: 'Set to 10 hours',
        value: 10,
        field: 'legalMaxHoursPerDay',
      },
    });
  }

  // ============================================
  // CHECK 15: No Deadline Reminders
  // ============================================
  if (!settings.deadline?.sendReminders) {
    findings.push({
      id: 'no-deadline-reminders',
      severity: 'warning',
      category: 'usability',
      title: 'Deadline Reminders Disabled',
      description: 'Users will not receive any reminders about upcoming timesheet deadlines.',
      recommendation: 'Enable deadline reminders to improve submission rates and reduce late submissions.',
      affectedSetting: 'deadline.sendReminders',
      affectedTab: 'submissions-deadlines',
      fixAction: {
        label: 'Enable reminders',
        value: true,
        field: 'sendReminders',
      },
    });
  }

  // ============================================
  // Calculate Health Score
  // ============================================
  const errorCount = findings.filter(f => f.severity === 'error').length;
  const warningCount = findings.filter(f => f.severity === 'warning').length;
  const infoCount = findings.filter(f => f.severity === 'info').length;
  
  // Scoring: Start at 100, deduct points for issues
  // Errors: -20 points each
  // Warnings: -10 points each
  // Info: -5 points each
  const score = Math.max(0, 100 - (errorCount * 20) - (warningCount * 10) - (infoCount * 5));

  return {
    findings,
    score,
    summary: {
      errors: errorCount,
      warnings: warningCount,
      suggestions: infoCount,
    },
    timestamp: new Date(),
  };
};
